import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { downloadCsvMatrix, type CsvCell } from "@/lib/csv";
import type { Customer, StaffMember } from "@/pages/admin/customers/hooks/useCustomers";

interface FollowUpBoardProps {
  customers: Customer[];
  loading: boolean;
  staff: StaffMember[];
  onBulkAssign: (userIds: string[], assignee: string) => Promise<{ assigned: number; skipped: number }>;
}

interface BoardGroup {
  key: string;
  label: string;
  icon: string;
  tone: "accent" | "secondary" | "primary";
}

interface WorkloadRow {
  name: string;
  total: number;
  risky: number;
  assigned: boolean;
}

interface RiskAssigneeRow {
  name: string;
  assigned: boolean;
  total: number;
  inactive: number;
  swapping: number;
  followed: number;
}

const GROUPS: BoardGroup[] = [
  { key: "inactive", label: "长期无活跃", icon: "ri-zzz-line", tone: "accent" },
  { key: "swapping", label: "频繁换机", icon: "ri-swap-box-line", tone: "secondary" },
  { key: "none", label: "无风险标记", icon: "ri-shield-check-line", tone: "primary" },
];

const TONE_ICON: Record<string, string> = {
  accent: "bg-accent-100 text-accent-700",
  secondary: "bg-secondary-100 text-secondary-900",
  primary: "bg-primary-100 text-primary-700",
};

const TONE_DOT: Record<string, string> = {
  accent: "bg-accent-500",
  secondary: "bg-secondary-500",
  primary: "bg-primary-500",
};

const TONE_BAR: Record<string, string> = {
  accent: "bg-accent-500",
  secondary: "bg-secondary-500",
  primary: "bg-primary-500",
};

const UNASSIGNED = "未分配";

function groupKey(customer: Customer): string {
  if (customer.riskFlags.some((flag) => flag.key === "inactive")) return "inactive";
  if (customer.riskFlags.some((flag) => flag.key === "swapping")) return "swapping";
  return "none";
}

export default function FollowUpBoard({ customers, loading, staff, onBulkAssign }: FollowUpBoardProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [assignDraft, setAssignDraft] = useState<Record<string, string>>({});
  const [assignBusy, setAssignBusy] = useState<string | null>(null);
  const [exported, setExported] = useState(false);

  // 点击负责人工作量条目：把该负责人写入 URL 筛选参数，并滚动到下方客户列表。
  const handleViewList = (assignee: string) => {
    const next = new URLSearchParams(searchParams);
    next.delete("filter");
    if (assignee === "none") next.set("assignee", "none");
    else next.set("assignee", assignee);
    setSearchParams(next, { replace: true });
    window.setTimeout(() => {
      document.getElementById("customer-list")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
  };

  const rows = GROUPS.map((group) => {
    const list = customers.filter((customer) => groupKey(customer) === group.key);
    const following = list.filter((customer) => customer.followUp).length;
    const assigned = list.filter((customer) => customer.followUp && customer.followUpAssignee).length;
    return { ...group, total: list.length, following, assigned, pending: list.length - following };
  });

  const totalFollowing = customers.filter((customer) => customer.followUp).length;
  const riskyTotal = customers.filter((customer) => customer.riskFlags.length > 0).length;
  const coverage = customers.length === 0 ? 0 : Math.round((totalFollowing / customers.length) * 100);

  // 负责人工作量：统计每个负责人名下跟进中客户数与其中带风险标记的数量。
  const workloadMap = new Map<string, { total: number; risky: number; assigned: boolean }>();
  customers
    .filter((customer) => customer.followUp)
    .forEach((customer) => {
      const key = customer.followUpAssignee?.trim() || UNASSIGNED;
      const item = workloadMap.get(key) ?? { total: 0, risky: 0, assigned: key !== UNASSIGNED };
      item.total += 1;
      if (customer.riskFlags.length > 0) item.risky += 1;
      workloadMap.set(key, item);
    });

  const workloadRows: WorkloadRow[] = Array.from(workloadMap.entries())
    .map(([name, value]) => ({ name, total: value.total, risky: value.risky, assigned: value.assigned }))
    .sort((a, b) => b.total - a.total || b.risky - a.risky || a.name.localeCompare(b.name, "zh-CN"));

  const maxWorkload = workloadRows.length > 0 ? workloadRows[0].total : 1;
  const totalAssigned = customers.filter((customer) => customer.followUp && customer.followUpAssignee).length;
  const unassigned = totalFollowing - totalAssigned;

  // 负责人 × 风险标记组合视角：每位负责人名下待处理的风险客户（含风险类型拆分）。
  const riskMap = new Map<string, RiskAssigneeRow>();
  customers
    .filter((customer) => customer.riskFlags.length > 0)
    .forEach((customer) => {
      const key = customer.followUpAssignee?.trim() || UNASSIGNED;
      const row = riskMap.get(key) ?? {
        name: key,
        assigned: key !== UNASSIGNED,
        total: 0,
        inactive: 0,
        swapping: 0,
        followed: 0,
      };
      row.total += 1;
      if (customer.riskFlags.some((flag) => flag.key === "inactive")) row.inactive += 1;
      if (customer.riskFlags.some((flag) => flag.key === "swapping")) row.swapping += 1;
      if (customer.followUp) row.followed += 1;
      riskMap.set(key, row);
    });

  const riskAssigneeRows: RiskAssigneeRow[] = Array.from(riskMap.values()).sort(
    (a, b) => b.total - a.total || Number(a.assigned) - Number(b.assigned) || a.name.localeCompare(b.name, "zh-CN"),
  );
  const maxRisk = riskAssigneeRows.length > 0 ? riskAssigneeRows[0].total : 1;
  const unassignedRisk = riskAssigneeRows.find((row) => !row.assigned)?.total ?? 0;

  // 责任人下拉候选：管理员名单 + 已有负责人值（去重排序）。
  const assigneeOptions = useMemo(() => {
    const set = new Set<string>();
    staff.forEach((member) => set.add(member.name));
    customers.forEach((customer) => {
      if (customer.followUpAssignee) set.add(customer.followUpAssignee);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, "zh-CN"));
  }, [customers, staff]);

  // 某风险分组下可批量指派的客户（游客申请无账号，无法分配）。
  const riskTargetsFor = (name: string): Customer[] =>
    customers.filter(
      (customer) =>
        customer.riskFlags.length > 0 && (customer.followUpAssignee?.trim() || UNASSIGNED) === name && customer.userId,
    );

  const handleBulkAssign = async (name: string) => {
    const assignee = assignDraft[name];
    if (!assignee) return;
    const ids = riskTargetsFor(name)
      .map((customer) => customer.userId)
      .filter((id): id is string => Boolean(id));
    if (ids.length === 0) return;
    setAssignBusy(name);
    try {
      await onBulkAssign(ids, assignee);
      setAssignDraft((prev) => ({ ...prev, [name]: "" }));
    } finally {
      setAssignBusy(null);
    }
  };

  // 看板整体导出：负责人工作量 + 负责人 × 风险标记，两段表合一，供主管做周报。
  const handleExportBoard = () => {
    const stamp = new Date().toISOString().slice(0, 10);
    const matrix: CsvCell[][] = [];
    matrix.push([`LicenseHub 跟进进度看板 · 导出时间 ${new Date().toLocaleString("zh-CN")}`]);
    matrix.push([]);
    matrix.push(["负责人工作量"]);
    matrix.push(["排名", "负责人", "跟进中客户数", "其中风险客户数", "是否已分配"]);
    workloadRows.forEach((row, index) => {
      matrix.push([index + 1, row.name, row.total, row.risky, row.assigned ? "已分配" : "未分配"]);
    });
    if (workloadRows.length === 0) matrix.push(["（暂无跟进中客户）"]);
    matrix.push([]);
    matrix.push(["负责人 × 风险标记 · 风险客户待处理分布"]);
    matrix.push(["负责人", "风险客户总数", "长期无活跃", "频繁换机", "已跟进", "待跟进", "是否已分配"]);
    riskAssigneeRows.forEach((row) => {
      matrix.push([
        row.name,
        row.total,
        row.inactive,
        row.swapping,
        row.followed,
        row.total - row.followed,
        row.assigned ? "已分配" : "未分配",
      ]);
    });
    if (riskAssigneeRows.length === 0) matrix.push(["（暂无风险客户）"]);
    matrix.push([]);
    matrix.push(["汇总", `跟进中 ${totalFollowing}`, `风险客户 ${riskyTotal}`, `覆盖率 ${coverage}%`]);

    downloadCsvMatrix(`跟进看板周报_${stamp}.csv`, matrix);
    setExported(true);
    window.setTimeout(() => setExported(false), 2000);
  };

  return (
    <section className="rounded-lg border border-background-200 bg-background-50 p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 font-heading text-base font-semibold text-foreground-950">
            <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-100 text-primary-700">
              <i className="ri-flag-2-line" />
            </span>
            跟进进度看板
          </h2>
          <p className="mt-1 text-xs text-foreground-500">
            按风险标记分组，展示各组客户的跟进覆盖情况，并附带负责人工作量与「负责人 × 风险标记」分布，方便客服主管掌握整体进度。
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-800">
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className="ri-alarm-warning-line" />
            </span>
            风险客户 {loading ? "--" : riskyTotal}
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700">
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className="ri-flag-2-fill" />
            </span>
            跟进中 {loading ? "--" : totalFollowing}
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-900">
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className="ri-percent-line" />
            </span>
            覆盖率 {loading ? "--" : `${coverage}%`}
          </span>
          <button
            type="button"
            disabled={loading || (workloadRows.length === 0 && riskAssigneeRows.length === 0)}
            onClick={handleExportBoard}
            title="一键导出本看板全部数据（负责人工作量 + 负责人风险分布），供主管做周报"
            className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-1 text-xs font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <span className="flex h-3.5 w-3.5 items-center justify-center text-primary-700">
              <i className={exported ? "ri-check-line" : "ri-download-2-line"} />
            </span>
            {exported ? "已导出" : "导出看板周报"}
          </button>
        </div>
      </div>

      <div className="mt-4 grid gap-3 md:grid-cols-3">
        {rows.map((row) => {
          const width = row.total === 0 ? 0 : Math.round((row.following / row.total) * 100);
          return (
            <div key={row.key} className="rounded-lg border border-background-200 bg-background-100 p-4">
              <div className="flex items-center justify-between gap-2">
                <span className="flex min-w-0 items-center gap-2 text-sm font-medium text-foreground-900">
                  <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-xs ${TONE_ICON[row.tone]}`}>
                    <i className={row.icon} />
                  </span>
                  <span className="truncate">{row.label}</span>
                </span>
                <span className="flex shrink-0 items-center gap-1 text-xs text-foreground-500">
                  <span className={`h-1.5 w-1.5 rounded-full ${TONE_DOT[row.tone]}`} />
                  共 {loading ? "--" : row.total}
                </span>
              </div>

              <div className="mt-3 flex items-end justify-between">
                <div>
                  <p className="text-[11px] text-foreground-500">跟进中</p>
                  <p className="mt-0.5 font-heading text-2xl font-semibold text-foreground-950">
                    {loading ? "--" : row.following}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-[11px] text-foreground-500">未跟进</p>
                  <p className="mt-0.5 font-heading text-2xl font-semibold text-foreground-700">
                    {loading ? "--" : row.pending}
                  </p>
                </div>
              </div>

              <div className="mt-3">
                <div className="h-2 w-full overflow-hidden rounded-full bg-background-200">
                  <div
                    className={`h-full rounded-full transition-all ${TONE_BAR[row.tone]}`}
                    style={{ width: `${loading ? 0 : width}%` }}
                  />
                </div>
                <div className="mt-1.5 flex items-center justify-between text-[11px] text-foreground-500">
                  <span>覆盖率 {loading ? "--" : `${width}%`}</span>
                  <span className="flex items-center gap-1">
                    <span className="flex h-3 w-3 items-center justify-center text-primary-700">
                      <i className="ri-user-star-line" />
                    </span>
                    {loading ? "--" : row.assigned} 位已分配负责人
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-5 border-t border-background-200 pt-4">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <p className="flex items-center gap-2 text-sm font-medium text-foreground-950">
            <span className="flex h-4 w-4 items-center justify-center text-primary-700">
              <i className="ri-team-line" />
            </span>
            负责人工作量 · 谁手上单子最多
          </p>
          <div className="flex flex-wrap items-center gap-2 text-[11px]">
            <span className="rounded-full bg-primary-100 px-2.5 py-0.5 font-medium text-primary-700">
              已分配 {loading ? "--" : totalAssigned}
            </span>
            <span className="rounded-full bg-secondary-100 px-2.5 py-0.5 font-medium text-secondary-900">
              未分配 {loading ? "--" : unassigned}
            </span>
          </div>
        </div>

        {workloadRows.length === 0 ? (
          <p className="rounded-md border border-dashed border-background-300 bg-background-100 px-4 py-6 text-center text-xs text-foreground-500">
            暂无跟进中的客户，负责人工作量会在客户加入跟进清单后自动统计。
          </p>
        ) : (
          <ul className="flex flex-col gap-2">
            {workloadRows.map((row, index) => {
              const width = Math.max(4, Math.round((row.total / maxWorkload) * 100));
              const unassignedRow = !row.assigned;
              return (
                <li key={row.name}>
                  <button
                    type="button"
                    onClick={() => handleViewList(unassignedRow ? "none" : row.name)}
                    title={
                      unassignedRow
                        ? "查看未分配负责人的跟进客户"
                        : `查看「${row.name}」的跟进客户`
                    }
                    className="flex w-full cursor-pointer items-center gap-3 rounded-md px-1.5 py-1.5 text-left transition-colors hover:bg-background-100"
                  >
                    <span
                      className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-[11px] font-medium ${
                        index === 0 && !unassignedRow
                          ? "bg-primary-500 text-background-50"
                          : "bg-background-100 text-foreground-600"
                      }`}
                    >
                      {index + 1}
                    </span>
                    <span
                      className="flex w-20 shrink-0 items-center gap-1 truncate text-xs text-foreground-900 sm:w-28"
                      title={row.name}
                    >
                      {unassignedRow ? (
                        <span className="flex h-3.5 w-3.5 shrink-0 items-center justify-center text-foreground-400">
                          <i className="ri-user-unfollow-line" />
                        </span>
                      ) : (
                        <span className="flex h-3.5 w-3.5 shrink-0 items-center justify-center text-secondary-700">
                          <i className="ri-user-star-line" />
                        </span>
                      )}
                      <span className="truncate">{row.name}</span>
                    </span>
                    <div className="h-2 flex-1 overflow-hidden rounded-full bg-background-100">
                      <div
                        className={`h-full rounded-full transition-all ${
                          unassignedRow ? "bg-secondary-300" : "bg-primary-500"
                        }`}
                        style={{ width: `${loading ? 0 : width}%` }}
                      />
                    </div>
                    <span className="w-14 shrink-0 text-right text-xs font-medium text-foreground-950">
                      {loading ? "--" : row.total} 位
                    </span>
                    {row.risky > 0 ? (
                      <span
                        title={`其中 ${row.risky} 位带风险标记`}
                        className="inline-flex w-16 shrink-0 items-center justify-end gap-0.5 text-[10px] font-medium text-accent-700"
                      >
                        <span className="flex h-3 w-3 items-center justify-center">
                          <i className="ri-alarm-warning-line" />
                        </span>
                        {row.risky} 风险
                      </span>
                    ) : (
                      <span className="w-16 shrink-0 text-right text-[10px] text-foreground-400">无风险</span>
                    )}
                    <span className="hidden shrink-0 items-center gap-0.5 rounded-full bg-primary-50 px-2 py-0.5 text-[10px] font-medium text-primary-700 sm:inline-flex">
                      查看清单
                      <span className="flex h-3 w-3 items-center justify-center">
                        <i className="ri-arrow-right-s-line" />
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="mt-5 border-t border-background-200 pt-4">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <p className="flex items-center gap-2 text-sm font-medium text-foreground-950">
            <span className="flex h-4 w-4 items-center justify-center text-accent-700">
              <i className="ri-alarm-warning-line" />
            </span>
            负责人 × 风险标记 · 风险客户待处理分布
          </p>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-100 px-2.5 py-0.5 text-[11px] font-medium text-accent-800">
            未分配风险 {loading ? "--" : unassignedRisk} 位
          </span>
        </div>

        {riskAssigneeRows.length === 0 ? (
          <p className="rounded-md border border-dashed border-background-300 bg-background-100 px-4 py-6 text-center text-xs text-foreground-500">
            暂无风险客户，识别到「长期无活跃 / 频繁换机」后会自动按负责人归集。
          </p>
        ) : (
          <div className="grid gap-3 md:grid-cols-2">
            {riskAssigneeRows.map((row) => {
              const width = Math.max(4, Math.round((row.total / maxRisk) * 100));
              const targets = riskTargetsFor(row.name);
              const draft = assignDraft[row.name] ?? "";
              const busy = assignBusy === row.name;
              return (
                <div
                  key={row.name}
                  className={`rounded-lg border p-4 ${
                    row.assigned ? "border-background-200 bg-background-100" : "border-accent-300 bg-accent-50"
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="flex min-w-0 items-center gap-2 text-sm font-medium text-foreground-900">
                      <span
                        className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-xs ${
                          row.assigned ? "bg-secondary-100 text-secondary-900" : "bg-accent-100 text-accent-700"
                        }`}
                      >
                        <i className={row.assigned ? "ri-user-star-line" : "ri-user-unfollow-line"} />
                      </span>
                      <span className="truncate" title={row.name}>
                        {row.name}
                      </span>
                    </span>
                    <span className="shrink-0 text-right">
                      <span className="font-heading text-xl font-semibold text-foreground-950">
                        {loading ? "--" : row.total}
                      </span>
                      <span className="ml-1 text-[11px] text-foreground-500">位风险</span>
                    </span>
                  </div>

                  <div className="mt-3">
                    <div className="h-2 w-full overflow-hidden rounded-full bg-background-200">
                      <div
                        className={`h-full rounded-full transition-all ${row.assigned ? "bg-secondary-500" : "bg-accent-500"}`}
                        style={{ width: `${loading ? 0 : width}%` }}
                      />
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]">
                      <span className="inline-flex items-center gap-1 text-accent-700">
                        <span className="flex h-3 w-3 items-center justify-center">
                          <i className="ri-zzz-line" />
                        </span>
                        长期无活跃 {loading ? "--" : row.inactive}
                      </span>
                      <span className="inline-flex items-center gap-1 text-secondary-900">
                        <span className="flex h-3 w-3 items-center justify-center">
                          <i className="ri-swap-box-line" />
                        </span>
                        频繁换机 {loading ? "--" : row.swapping}
                      </span>
                      <span className="inline-flex items-center gap-1 text-foreground-500">
                        <span className="flex h-3 w-3 items-center justify-center">
                          <i className="ri-flag-2-line" />
                        </span>
                        已跟进 {loading ? "--" : row.followed}/{loading ? "--" : row.total}
                      </span>
                    </div>
                  </div>

                  <div className="mt-3 flex flex-col gap-2 border-t border-background-200 pt-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="relative min-w-[140px] flex-1">
                        <span className="pointer-events-none absolute left-2.5 top-1/2 flex h-3.5 w-3.5 -translate-y-1/2 items-center justify-center text-primary-700">
                          <i className="ri-user-add-line" />
                        </span>
                        <select
                          value={draft}
                          disabled={busy || targets.length === 0}
                          onChange={(event) =>
                            setAssignDraft((prev) => ({ ...prev, [row.name]: event.target.value }))
                          }
                          aria-label={`把「${row.name}」这组风险客户指派给负责人`}
                          className="w-full cursor-pointer appearance-none rounded-md border border-background-300 bg-background-50 py-1.5 pl-7 pr-7 text-xs text-foreground-800 focus:border-primary-400 focus:outline-none disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          <option value="">指派给…</option>
                          {assigneeOptions.map((name) => (
                            <option key={name} value={name}>
                              {name}
                            </option>
                          ))}
                        </select>
                        <span className="pointer-events-none absolute right-2 top-1/2 flex h-3.5 w-3.5 -translate-y-1/2 items-center justify-center text-foreground-400">
                          <i className="ri-arrow-down-s-line" />
                        </span>
                      </div>
                      <button
                        type="button"
                        disabled={!draft || busy || targets.length === 0}
                        onClick={() => {
                          void handleBulkAssign(row.name);
                        }}
                        title={
                          targets.length === 0
                            ? "该组暂无可指派客户（游客申请无账号）"
                            : `把该组 ${targets.length} 位风险客户一键指派给所选负责人`
                        }
                        className="inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-3 py-1.5 text-[11px] font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <span className="flex h-3.5 w-3.5 items-center justify-center">
                          <i className={busy ? "ri-loader-4-line animate-spin" : "ri-share-forward-line"} />
                        </span>
                        {busy ? "指派中…" : "批量指派"}
                      </button>
                    </div>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      {row.assigned ? (
                        <button
                          type="button"
                          onClick={() => handleViewList(row.name)}
                          className="inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-secondary-200 bg-secondary-50 px-2.5 py-1 text-[11px] font-medium text-secondary-900 transition-colors hover:bg-secondary-100"
                        >
                          <span className="flex h-3.5 w-3.5 items-center justify-center">
                            <i className="ri-list-check" />
                          </span>
                          查看此人风险客户
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleViewList("none")}
                          className="inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-accent-300 bg-accent-50 px-2.5 py-1 text-[11px] font-medium text-accent-800 transition-colors hover:bg-accent-100"
                        >
                          <span className="flex h-3.5 w-3.5 items-center justify-center">
                            <i className="ri-user-add-line" />
                          </span>
                          查看待分配风险客户
                        </button>
                      )}
                      <span className="text-[10px] text-foreground-400">
                        该组 {targets.length} 位可指派
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}